import { Paper } from "@mui/material";
import { useEnvContext } from "context/EnvContext";
import {
  APP_CAREPROVIDER,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import { getProviderSearchAccountName } from "core/model/accounts";
import { getRequestOrigin } from "core/model/requests";
import { useGetTranslationForSearchType } from "core/model/utils/ontologies/hooks";
import { AuctionRequest, QueryProgress } from "core/types";
import { Banner } from "ds/ui";
import { EncryptedHint } from "ds_legacy/components/EncryptedHint";
import { RSButtonColor, RSButtonVariant } from "ds_legacy/components/RSButton";
import RSButton from "ds_legacy/components/RSButton/RSButton";
import TextAreaInputField from "ds_legacy/components/TextArea/TextAreaForm";
import { GREY_50 } from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import {
  Z_INDEX_MESSENGER,
  dp,
  margin,
  padding,
  space,
} from "ds_legacy/materials/metrics";
import { useEncryption } from "dsl/atoms/Contexts";
import MessageRecipientHint from "dsl/atoms/MessageRecipientHint";
import { TermsAndConditions } from "dsl/atoms/TermsAndConditions";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import {
  BANNER_PADDING,
  BannerTextAreaBox,
  bannerActionStyle,
} from "dsl/organisms/Banners";
import {
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  Form,
  ValidationFunction,
  composeValidation,
  convertIn,
  convertModelDefinition,
  convertOut,
  validateModel,
} from "react-forms-state";
import styled from "styled-components";
import { useTranslations } from "translations";
import Translations from "translations/types";

export type MessengerFormProps = {
  auctionRequest: AuctionRequest;
  closeForm: () => void;
  newerTCsAvailable?: boolean;
  titleId: string;
};

const BANNER_TEXT_AREA_WIDTH = `calc(100% - ${space(BANNER_PADDING - 3.5)})`;

const FormBannerBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const FormAreaDiv = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: column;
  flex: 1 0 0px;
  height: 90%;
  margin: ${({ margin }) => margin || undefined};
  width: 100%;
`;

export const Scrollable = styled.div`
  display: flex;
  overflow: auto;
  flex: 1 1 auto;
`;

export const TextFieldWrapper = styled.div<{ margin?: string }>`
  margin: ${(props) => props.margin || margin(2, 2, 4, 2)};
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`;

export const FormWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${padding(0, 1)};
  height: 100%;
`;

const GhostButton = styled.div`
  height: ${dp(36)};
  width: ${dp(120)};
  margin: ${margin(1, 0)};
`;

export function MessengerNonModalDialog({
  ariaLabelledBy,
  children,
}: {
  ariaLabelledBy: string;
  children: ReactNode;
  fallbackRestoreRef?: MutableRefObject<HTMLDivElement | null>;
}) {
  const fallbackRestoreEl = document.getElementById("message_input");
  const focusRef = useFocusElement<HTMLDivElement>({
    fallbackRestoreRef: fallbackRestoreEl,
    restoreFocus: true,
    setTabIndex: true,
    setTabIndexFallbackRestoreRef: false,
  });

  return (
    <Paper
      ref={focusRef as any}
      aria-labelledby={ariaLabelledBy}
      aria-modal={false}
      role="dialog"
      sx={{
        alignItems: "center",
        background: GREY_50,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        top: 0,
        width: "100%",
        zIndex: Z_INDEX_MESSENGER + 1,
      }}
    >
      <FormBannerBox>{children}</FormBannerBox>
    </Paper>
  );
}

export const FormArea = ({
  children,
  margin,
}: {
  children: ReactNode;
  margin?: string;
}) => (
  <Scrollable>
    <FormAreaDiv margin={margin}>{children}</FormAreaDiv>
  </Scrollable>
);

export function ConnectedButton({
  color,
  customStyle,
  isDisabled,
  isInverted = false,
  onSubmit,
  onSubmitQueryProgress = QUERY_PROGRESS_NOT_STARTED,
  shouldHide = false,
  submitText,
  variant,
}: {
  color?: RSButtonColor;
  customStyle?: CSSProperties;
  isDisabled?: boolean;
  isInverted?: boolean;
  onSubmit: (args: ToType) => void;
  onSubmitQueryProgress: QueryProgress;
  shouldHide?: boolean;
  submitText: string;
  variant: RSButtonVariant;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const translations = useTranslations();

  useEffect(() => {
    setIsLoading(onSubmitQueryProgress === QUERY_PROGRESS_PENDING);
  }, [onSubmitQueryProgress]);

  if (shouldHide) return <GhostButton />;

  return (
    <RSButton
      color={color}
      disabled={isDisabled}
      id="confirm_decline"
      inverted={isInverted}
      loading={onSubmitQueryProgress}
      style={{ ...bannerActionStyle, ...customStyle }}
      variant={isLoading ? "contained" : variant}
      onClick={onSubmit}
    >
      {isLoading ? translations.actions.saving : submitText}
    </RSButton>
  );
}

export function TextArea({
  elementName,
  endAdornment,
  isForMessenger = false,
  isRequired = false,
  isTest = false,
  label,
  placeholder,
  rows = 9,
  showError = false,
}: {
  elementName: string;
  endAdornment?: ReactNode;
  isForMessenger?: boolean;
  isRequired?: boolean;
  isTest?: boolean;
  label?: string;
  placeholder?: string;
  rows?: number;
  showError?: boolean;
}) {
  return (
    <TextAreaInputField
      elementName={elementName}
      endAdornment={endAdornment}
      hideError={!showError}
      label={label}
      margin={isForMessenger ? "dense" : "normal"}
      placeholder={placeholder}
      required={isRequired}
      rows={isTest ? 1 : rows}
      sx={{ width: `calc(100% - ${space(BANNER_PADDING - 3.5)})` }}
    />
  );
}

export function PreferredProviderBanner({
  auctionRequest,
}: {
  auctionRequest: AuctionRequest;
}) {
  const { app } = useEnvContext();
  const getTranslationForSearchType = useGetTranslationForSearchType();
  const preferredSelectionTranslation = getTranslationForSearchType({
    receiverType: "receiverTypeSingularPreferred",
    searchType: auctionRequest.auction?.search_type,
    translationKey: "auctionRequest.preferredSelection",
  });

  if (app !== APP_CAREPROVIDER || !auctionRequest?.patient_preferred) {
    return null;
  }

  return <Banner color="success" message={preferredSelectionTranslation} />;
}

export const BasicForm = (definition: ToType, validation?: ToType) => {
  const modelDefinition = convertModelDefinition(definition);

  const validateForm: ValidationFunction =
    validation ||
    function validate() {
      return true;
    };

  return Form({
    convertIn: convertIn(modelDefinition),
    convertOut: convertOut(modelDefinition),
    validate: (value: string, props: ToType) =>
      composeValidation(validateForm, validateModel(modelDefinition))(
        value,
        props,
      ),
  });
};

export const getDirectPatientName = (
  auctionRequest: AuctionRequest,
  translations: Translations,
): string | null => {
  const { isProviderSearchRequest } = getRequestOrigin(auctionRequest);

  return isProviderSearchRequest
    ? getProviderSearchAccountName({
        patientId: auctionRequest?.auction?.patient?.user_id,
        translations,
      })
    : null;
};

export const MessageFormView = ({
  auctionRequest,
  elementName,
  withTermsAndConditions,
}: {
  auctionRequest: AuctionRequest;
  elementName: string;
  withTermsAndConditions?: boolean;
}) => {
  const translations = useTranslations();
  const { decryptedSessionKey, isSealdOnly } = useEncryption();

  if (!auctionRequest?.careprovider) {
    console.error(
      "error no careprovider in auction request in MessageFormView",
    );
    return null;
  }
  const directPatient = getDirectPatientName(auctionRequest, translations);
  const { isAdmissionsRequest } = getRequestOrigin(auctionRequest);

  if (isAdmissionsRequest) {
    return null;
  }

  return (
    <VerticalLayout>
      <MessageRecipientHint
        careprovider={auctionRequest.careprovider}
        careseeker={auctionRequest.auction?.patient?.careseeker}
        directPatient={directPatient}
        isMessenger
      />
      <EncryptedHint
        customMargin={margin(2, 0, -1, 2)}
        isEncryptionActivated={isSealdOnly || decryptedSessionKey != null}
      />
      <TextFieldWrapper margin={margin(0, 2)}>
        <TextArea
          elementName={elementName}
          label={translations.auctionRequest.typeMessageHereOptional}
          placeholder={
            translations.auctionRequest.acceptOptionalMessagePlaceholder
          }
          rows={3}
          showError
        />
      </TextFieldWrapper>
      {withTermsAndConditions && (
        <VerticalLayout data-testid="actions-box">
          <TermsAndConditions />
        </VerticalLayout>
      )}
    </VerticalLayout>
  );
};

export function MessageArea({
  elementName,
  endAdornment,
  isRequired,
  label,
}: {
  elementName: string;
  endAdornment?: ReactNode;
  isRequired?: boolean;
  label: string;
}) {
  return (
    <BannerTextAreaBox>
      <TextAreaInputField
        elementName={elementName}
        endAdornment={endAdornment}
        label={label}
        required={isRequired}
        rows={9}
        sx={{ width: BANNER_TEXT_AREA_WIDTH }}
      />
    </BannerTextAreaBox>
  );
}
